'use client';

import { c as _c } from "react/compiler-runtime";
import { Skeleton } from '@common/components/atom/Skeleton/Skeleton';
import { cn } from '@core/utils';
import type { GiftOrderProductReviewDto } from '@generated/auth';
import { useQuery } from '@tanstack/react-query';
import { Suspense } from 'react';
import { UnauthQueryOptions } from '@/api';
import type { GiftProductDetail } from '@/api/getGiftProductDetail';
import { EventRewardCard } from '@/app/donation/_components/EventRewardCard/EventRewardCard';
import { DONATION_DESKTOP_STICKY_TOP } from '@/app/donation/constants';
import type { GiftSaleStatus } from '@/app/donation/types';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useEditionGoodsGift } from '../../_hooks/useEditionGoodsGift';
import { TABS } from '../../constants';
import { GiftDetailTab } from '../GiftDetailTab';
import { GiftTabContentBasic } from '../GiftTabContent/_components/GiftTabContentBasic';
import { GiftTabContentDelivery } from '../GiftTabContent/_components/GiftTabContentDelivery';
import { GiftTabContentInfo } from '../GiftTabContent/_components/GiftTabContentInfo';
import { GiftTabContentInquiry } from '../GiftTabContent/_components/GiftTabContentInquiry';
import { GiftTabContentReview } from '../GiftTabContent/_components/GiftTabContentReview';
import { HeaderInfoSection } from './HeaderInfoSection';
import { PrefillForm } from './PrefillForm';
import { ResponsiveDivider } from './ResponsiveDivider';
import { ReviewShortcutSection } from './ReviewShortcutSection';
import { useSelectedDonationAmount } from './useSelectedDonationAmount';
interface GiftDetailTemplateProps {
  title: string;
  heroImageUrlList: string[];
  adultYN: boolean;
  institutionName: string;
  productSaleStatus: GiftSaleStatus;
  productName: string;
  productNumber: string;
  requiredDonationAmount: number;
  onlineProductYN: boolean;
  onlineProductDesc?: string;
  exchangeYN: boolean;
  reviewList: GiftOrderProductReviewDto[];
  reviewTotalCount: number;
  photoReviewList: {
    fileUrl: string;
    reviewId: number;
  }[];
  photoReviewCount: number;
  detailContentInfoList: GiftProductDetail['detailContentInfoList'];
  productInfo: string;
  deliveryCompanyName?: string;
  providerName?: string;
  providerBusinessNumber?: string;
  providerContactNumber?: string;
  providerAddress?: string;
  productData?: GiftProductDetail;
  noticeTitle?: string;
  noticeContents?: string;
}
const GIFT_DETAIL_SIDEBAR_STICKY_TOP = 218;
export const GiftDetailTemplate = t0 => {
  const $ = _c(85);
  const {
    title,
    heroImageUrlList,
    adultYN,
    institutionName,
    productSaleStatus,
    productName,
    productNumber,
    requiredDonationAmount,
    exchangeYN,
    onlineProductYN,
    onlineProductDesc,
    reviewList,
    reviewTotalCount,
    photoReviewList,
    photoReviewCount,
    detailContentInfoList,
    productInfo,
    deliveryCompanyName,
    providerName,
    providerBusinessNumber,
    providerContactNumber,
    providerAddress,
    productData,
    noticeTitle,
    noticeContents
  } = t0;
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  const goodsGift = useEditionGoodsGift(productData);
  const t1 = productData?.currentPrice;
  let t2;
  if ($[0] !== requiredDonationAmount || $[1] !== t1) {
    t2 = {
      currentPrice: t1,
      requiredDonationAmount
    };
    $[0] = requiredDonationAmount;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    totalDonationAmount
  } = useSelectedDonationAmount(t2);
  let t3;
  if ($[3] !== productNumber) {
    t3 = UnauthQueryOptions["get:/wello/v2/allows/home/gift-product/{productNumber}/review-stat"]({
      path: {
        productNumber
      }
    });
    $[3] = productNumber;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = {
      ...t3,
      select: _temp
    };
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    data: reviewStat
  } = useQuery(t4);
  const reviewCount = reviewStat?.reviewCount ?? 0;
  const reviewRating = reviewStat?.reviewRating;
  let t5;
  if ($[7] !== reviewCount) {
    t5 = [...(reviewCount < 1 ? [TABS.REVIEW] : [])];
    $[7] = reviewCount;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const disabledTabs = t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = cn("mt-20 content-container", "tablet:mt-0 tablet:px-0", "mobile:mt-0 mobile:px-0");
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cn("mb-100 px-40", "mobile-tablet:mb-20 mobile-tablet:px-0");
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== adultYN || $[12] !== exchangeYN || $[13] !== goodsGift || $[14] !== heroImageUrlList || $[15] !== institutionName || $[16] !== onlineProductDesc || $[17] !== onlineProductYN || $[18] !== productData || $[19] !== productName || $[20] !== productNumber || $[21] !== productSaleStatus || $[22] !== requiredDonationAmount || $[23] !== reviewCount || $[24] !== reviewRating || $[25] !== title) {
    t8 = <HeaderInfoSection title={title} heroImageUrlList={heroImageUrlList} adultYN={adultYN} institutionName={institutionName} productSaleStatus={productSaleStatus} productName={productName} reviewCount={reviewCount} reviewRating={reviewRating} productNumber={productNumber} requiredDonationAmount={requiredDonationAmount} exchangeYN={exchangeYN} onlineProductYN={onlineProductYN} onlineProductDesc={onlineProductDesc} productData={productData} goodsGift={goodsGift} className={t7} />;
    $[11] = adultYN;
    $[12] = exchangeYN;
    $[13] = goodsGift;
    $[14] = heroImageUrlList;
    $[15] = institutionName;
    $[16] = onlineProductDesc;
    $[17] = onlineProductYN;
    $[18] = productData;
    $[19] = productName;
    $[20] = productNumber;
    $[21] = productSaleStatus;
    $[22] = requiredDonationAmount;
    $[23] = reviewCount;
    $[24] = reviewRating;
    $[25] = title;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cn("mb-100 px-40", "mobile-tablet:hidden");
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== reviewCount || $[29] !== reviewList) {
    t10 = <ReviewShortcutSection reviewCount={reviewCount} items={reviewList} className={t9} />;
    $[28] = reviewCount;
    $[29] = reviewList;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = cn("sticky z-10 mx-40 bg-Gray-white", "mobile-tablet:top-[48px] mobile-tablet:mx-0");
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  let t12;
  if ($[32] !== isDesktopViewport) {
    t12 = isDesktopViewport ? {
      top: DONATION_DESKTOP_STICKY_TOP
    } : undefined;
    $[32] = isDesktopViewport;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== disabledTabs || $[35] !== productNumber || $[36] !== t12) {
    t13 = <GiftDetailTab productNumber={productNumber} disabledTabs={disabledTabs} className={t11} style={t12} />;
    $[34] = disabledTabs;
    $[35] = productNumber;
    $[36] = t12;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = cn("mt-40 grid grid-cols-[1fr_370px] gap-80 px-40", "mobile-tablet:mt-0 mobile-tablet:grid-cols-1 mobile-tablet:px-0");
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== detailContentInfoList || $[40] !== noticeContents || $[41] !== noticeTitle || $[42] !== productName) {
    t15 = <GiftTabContentBasic detailContentInfoList={detailContentInfoList} productName={productName} noticeTitle={noticeTitle} noticeContents={noticeContents} />;
    $[39] = detailContentInfoList;
    $[40] = noticeContents;
    $[41] = noticeTitle;
    $[42] = productName;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <ResponsiveDivider />;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== productInfo) {
    t17 = <GiftTabContentInfo productInfo={productInfo} />;
    $[45] = productInfo;
    $[46] = t17;
  } else {
    t17 = $[46];
  }
  let t18;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <ResponsiveDivider />;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== deliveryCompanyName || $[49] !== providerAddress || $[50] !== providerBusinessNumber || $[51] !== providerContactNumber || $[52] !== providerName) {
    t19 = <GiftTabContentDelivery deliveryCompanyName={deliveryCompanyName} providerName={providerName} providerBusinessNumber={providerBusinessNumber} providerContactNumber={providerContactNumber} providerAddress={providerAddress} />;
    $[48] = deliveryCompanyName;
    $[49] = providerAddress;
    $[50] = providerBusinessNumber;
    $[51] = providerContactNumber;
    $[52] = providerName;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  let t20;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <ResponsiveDivider />;
    $[54] = t20;
  } else {
    t20 = $[54];
  }
  let t21;
  if ($[55] !== photoReviewCount || $[56] !== photoReviewList || $[57] !== productNumber || $[58] !== reviewCount || $[59] !== reviewList || $[60] !== reviewRating || $[61] !== reviewTotalCount) {
    t21 = <GiftTabContentReview reviewRating={reviewRating} reviewCount={reviewCount} reviewList={reviewList} reviewTotalCount={reviewTotalCount} photoReviewList={photoReviewList} photoReviewCount={photoReviewCount} productNumber={productNumber} />;
    $[55] = photoReviewCount;
    $[56] = photoReviewList;
    $[57] = productNumber;
    $[58] = reviewCount;
    $[59] = reviewList;
    $[60] = reviewRating;
    $[61] = reviewTotalCount;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  let t22;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <ResponsiveDivider />;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  let t23;
  if ($[64] !== productNumber) {
    t23 = <GiftTabContentInquiry productNumber={productNumber} />;
    $[64] = productNumber;
    $[65] = t23;
  } else {
    t23 = $[65];
  }
  let t24;
  if ($[66] !== t15 || $[67] !== t17 || $[68] !== t19 || $[69] !== t21 || $[70] !== t23) {
    t24 = <div className="flex min-w-0 flex-col">{t15}{t16}{t17}{t18}{t19}{t20}{t21}{t22}{t23}</div>;
    $[66] = t15;
    $[67] = t17;
    $[68] = t19;
    $[69] = t21;
    $[70] = t23;
    $[71] = t24;
  } else {
    t24 = $[71];
  }
  let t25;
  if ($[72] !== isDesktopViewport || $[73] !== productData || $[74] !== productNumber || $[75] !== totalDonationAmount) {
    t25 = isDesktopViewport && <div><div className="sticky mb-44 rounded-[36px] border-1 px-28 py-44" style={{
        top: GIFT_DETAIL_SIDEBAR_STICKY_TOP
      }}><PrefillForm productData={productData} productNumber={productNumber} className="gap-80" /><Suspense fallback={<Skeleton className="mt-24 h-56 w-full rounded-medium" />}><EventRewardCard productNumber={productNumber} donationAmount={totalDonationAmount} defaultExpanded={true} className="mt-24" /></Suspense></div></div>;
    $[72] = isDesktopViewport;
    $[73] = productData;
    $[74] = productNumber;
    $[75] = totalDonationAmount;
    $[76] = t25;
  } else {
    t25 = $[76];
  }
  let t26;
  if ($[77] !== t24 || $[78] !== t25) {
    t26 = <div className={t14}>{t24}{t25}</div>;
    $[77] = t24;
    $[78] = t25;
    $[79] = t26;
  } else {
    t26 = $[79];
  }
  let t27;
  if ($[80] !== t10 || $[81] !== t13 || $[82] !== t26 || $[83] !== t8) {
    t27 = <div className={t6}>{t8}{t10}{t13}{t26}</div>;
    $[80] = t10;
    $[81] = t13;
    $[82] = t26;
    $[83] = t8;
    $[84] = t27;
  } else {
    t27 = $[84];
  }
  return t27;
};
function _temp(t0) {
  const {
    context
  } = t0;
  return context;
}